import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {useSelector} from 'react-redux';
import {find} from '../../../lib/objectHelper';

import {NormalizationPreviewWellStatus, WellCount, WellIncrementPattern} from '../../../constants/dataEnum';
import {isColorLight} from '../../../lib/colorHelper';

const getTextColor = (colorHex) => {
  try {
    return isColorLight(colorHex) ? '#000000' : '#ffffff';
  } catch (err) {
    return '#000000';
  }
};

const getBackgroundColor = (status, opacity = 1) => {
  const opacityHex = Math.ceil(opacity * 255).toString(16);
  let color;
  switch (status) {
    case NormalizationPreviewWellStatus.INVALID_INPUT:
      color = '#ffd1d1';
      break;
    case NormalizationPreviewWellStatus.INVALID:
      color = '#ff5252';
      break;
    case NormalizationPreviewWellStatus.SKIPPED:
      color = '#FFC200';
      break;
    case NormalizationPreviewWellStatus.NORMAL:
      color = `#006B2E${opacityHex}`;
      break;
    default:
      color = '#fff';
      break;
  }
  return color;
};

const Labware = styled.div`
  height: 100%;
  width: 100%;
  border: 2px solid #414141;
  background: #f5f5f5;
  display: grid;
  grid-template-areas:
    '. upperIndex'
    'lefIndex wellArea';
  grid-template-rows: 40px auto 30px;
  grid-template-columns: 40px auto 30px;
`;

const UpperIndex = styled.div`
  grid-area: upperIndex;
  display: grid;
  grid-template-columns: ${(prop) => `repeat(${prop.colCount}, 1fr)`};
  font-size: ${(prop) => (prop.wellCount === WellCount.WELL_96 ? '14px' : '12px')};
`;

const LeftIndex = styled.div`
  grid-area: lefIndex;
  display: grid;
  grid-template-rows: ${(prop) => `repeat(${prop.rowCount}, 1fr)`};
  font-size: ${(prop) => (prop.wellCount === WellCount.WELL_96 ? '14px' : '12px')};
`;

const WellArea = styled.div`
  width: 100%;
  grid-area: wellArea;
  display: grid;
  grid-template-columns: ${(prop) => `repeat(${prop.colCount}, 1fr)`};
  grid-template-rows: ${(prop) => `repeat(${prop.rowCount}, 1fr)`};
  /* border: 1px solid #414141; */
`;

const Index = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Well = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #414141;
  background: ${(prop) => {
    const opacity = prop.level ? prop.level : 1;
    let color = '#fff';

    if (prop.showColor) {
      color = getBackgroundColor(prop.status, opacity);
    }

    return color;
  }};
  color: ${(prop) => {
    const opacity = prop.level ? prop.level : 1;

    return getTextColor(getBackgroundColor(prop.status, opacity));
  }};
  cursor: pointer;
  font-weight: ${(prop) => (prop.highlighted ? 'bold' : 'unset')};
  font-size: ${(prop) => {
    if ((prop.wellCount !== WellCount.WELL_384 || prop.charCount <= 3) && prop.highlighted) {
      return '16px';
    }
    if (prop.wellCount === WellCount.WELL_384 && prop.highlighted && prop.charCount > 3) {
      return '11px';
    }
    if (prop.wellCount === WellCount.WELL_384 && prop.charCount > 3) {
      return '10px';
    }
    return 'unset';
  }};
`;

function LabwarePreview({showConcentration, showDiluent, handleClick, selectedLabwareWell}) {
  const {labwareState, normalizationState} = useSelector((state) => state);
  const labwareRef = useRef(null);
  const [labwareSize, setLabwareSize] = useState({width: 0, height: 0});

  const {
    sampleLabwareID,
    wellIncrementPattern,
    csvData,
    startRow,
    selectedColumnIndex,
    previewData,
    targetConcentration,
  } = normalizationState;

  const selectedLabware = useMemo(
    () => find(labwareState, {LabwareID: sampleLabwareID}) ?? null,
    [labwareState, sampleLabwareID],
  );

  useEffect(() => {
    if (labwareRef.current) {
      const {width} = labwareRef.current.getBoundingClientRect();

      // height:width ratio => 43 : 64
      const height = (5 / 7) * (width - 60);
      setLabwareSize({width: width - 60, height});
    }
  }, []);

  const generateUpperIndex = () => {
    const elements = [];
    for (let i = 0; i < selectedLabware?.colCount || 0; i += 1) {
      elements.push(<Index key={`upper-index${i}`}>{i + 1}</Index>);
    }

    return elements;
  };
  const generateLeftIndex = () => {
    const elements = [];
    for (let i = 0; i < selectedLabware?.rowCount || 0; i += 1) {
      elements.push(<Index key={`left-index${i}`}>{String.fromCharCode(65 + parseInt(i, 10))}</Index>);
    }

    return elements;
  };

  const getMaxMinData = (isConcentration, isDiluent) => {
    const {colCount, rowCount} = selectedLabware;
    const wellCount = colCount * rowCount + 1;
    let data;
    if (isConcentration) {
      data = csvData.slice(startRow, startRow + wellCount).map((row) => row[selectedColumnIndex]);
    }
    if (isDiluent) {
      data = previewData.map((row) => row.volume).filter((item) => typeof item === 'number' && item !== 0);
    }
    let maxData = 0;
    let minData = 0;

    if (data.length) {
      maxData = Math.max(...data);
      minData = Math.min(...data);
    }

    return [maxData, minData];
  };

  const generateWell = () => {
    const elements = [];
    const {colCount, rowCount} = selectedLabware;
    const wellCount = colCount * rowCount;
    let index = 0;
    const [maxData, minData] = getMaxMinData(showConcentration, showDiluent);

    for (let i = 0; i < rowCount; i += 1) {
      for (let j = 0; j < colCount; j += 1) {
        if (wellIncrementPattern === WellIncrementPattern.ORDER_BY_ROW) {
          index = j + 1 + colCount * i;
        } else if (wellIncrementPattern === WellIncrementPattern.ORDER_BY_COLUMN) {
          index = i + 1 + rowCount * j;
        }

        const handleClickWell = () => {
          let wellNumber = 0;
          if (wellIncrementPattern === WellIncrementPattern.ORDER_BY_ROW) {
            wellNumber = j + 1 + colCount * i;
          } else if (wellIncrementPattern === WellIncrementPattern.ORDER_BY_COLUMN) {
            wellNumber = i + 1 + rowCount * j;
          }
          handleClick(wellNumber);
        };

        if (selectedColumnIndex !== null && showConcentration) {
          const rowIndex = index + startRow - 1;
          const dataRow = csvData[rowIndex] ?? null;
          const data = dataRow ? dataRow[selectedColumnIndex] : null;

          const level = (data + 1 - minData) / (maxData + 1 - minData);

          const isValid = !Number.isNaN(Number(data)) && data !== Infinity && data !== 'Zero';

          elements.push(
            <Well
              key={`well-${index}`}
              wellCount={wellCount}
              status={isValid ? NormalizationPreviewWellStatus.NORMAL : NormalizationPreviewWellStatus.INVALID_INPUT}
              showColor={data !== null}
              level={level}
              onClick={handleClickWell}
              highlighted={selectedLabwareWell === index}
            >
              {data}
            </Well>,
          );
        }

        if (selectedColumnIndex === null) {
          elements.push(
            <Well key={`well-${index}`} wellCount={wellCount}>
              {index}
            </Well>,
          );
        }

        if (showDiluent) {
          const rowIndex = index - 1;
          const data = previewData[rowIndex] ?? null;

          // eslint-disable-next-line no-unsafe-optional-chaining
          const level = (data?.volume + 1 - minData) / (maxData + 1 - minData);
          const displayData = targetConcentration === 0 ? '-' : data?.volume;
          const displayDataCharCount = String(displayData).split('').length;

          elements.push(
            <Well
              key={`well-diluent-${index}`}
              status={data?.status}
              wellCount={wellCount}
              level={level}
              showColor={data !== null && targetConcentration !== 0}
              onClick={handleClickWell}
              highlighted={selectedLabwareWell === index}
              charCount={displayDataCharCount}
            >
              {displayData}
            </Well>,
          );
        }
      }
    }

    return elements;
  };

  return (
    <Labware ref={labwareRef} height={labwareSize.height}>
      <UpperIndex colCount={selectedLabware?.colCount ?? 0}>{generateUpperIndex()}</UpperIndex>
      <LeftIndex rowCount={selectedLabware?.rowCount ?? 0}>{generateLeftIndex()}</LeftIndex>
      <WellArea colCount={selectedLabware?.colCount ?? 0} rowCount={selectedLabware?.rowCount ?? 0}>
        {generateWell()}
      </WellArea>
    </Labware>
  );
}

LabwarePreview.propTypes = {
  showConcentration: PropTypes.bool,
  showDiluent: PropTypes.bool,
  handleClick: PropTypes.func,
  selectedLabwareWell: PropTypes.any,
};

LabwarePreview.defaultProps = {
  showConcentration: false,
  showDiluent: false,
  handleClick: () => {},
  selectedLabwareWell: null,
};

export default LabwarePreview;
